// BULK Trader Revamp
import { gql, useMutation, useQuery } from '@apollo/client';
import ConfirmationModal from '@components/modals/confirmationModal';
import BulkTradeRunAllocationsTable from '@components/tables/bulkTradeRunAllocationsTable/bulkTradeRunAllocationsTable';
import TradeRequestsTable from '@components/tables/tradeRequestsTable/tradeRequestsTable';
import { Exchanges } from '@interfaces/financialProduct';
import CheckCircleIcon from '@mui/icons-material/CheckCircle'; // no icons in ovComponents
import { Step, StepLabel, Stepper } from '@mui/material'; // no steps in ovComponents
import { ExternalLink } from '@ovComponents/1-primative/link/externalLink';
import { Card } from '@ovComponents/2-component/card/card';
import { AnalyticsContext } from '@providers/analyticsProvider';
import { ovAnalyticsEvents as analyticsEvents } from '@utils/analytics/analytics';
import {
  Box,
  Button,
  CircularProgress,
  Stack,
  Tooltip,
  Typography,
  useSnackbar,
} from 'hephaestus/web';
import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import {
  type AllocationFileRecord,
  type BulkTradeFileRecord,
  getAllocationFileDownloadLabel,
  getBulkTradeFileDownloadLabel,
} from '../bulkTradeFileLabels';
import { BulkTradeRunStates } from '../bulkTradeRunStates';
import {
  BULK_TRADER_UI_ONLY_STATES,
  resolveBulkTradeRunPolling,
  syncActiveStateFromBulkTradeRun,
} from '../bulkTraderPolling';
import UpdateMutualFundTradesTable from './components/updateMutualFundTradesTable';
import UpdateTradesTable from './components/updateTradesTable';

const FETCH_BULK_TRADE_RUN = gql`
  query fetchBulkTradeRun($bulkTradeRunId: ObjectID!, $PTF: Boolean) {
    fetchBulkTradeRun(bulkTradeRunId: $bulkTradeRunId) {
      bulkTradeRun {
        id
        createdAt
        buyBulkTradeFileUrl
        sellBulkTradeFileUrl
        mutualFundFileUrl
        state
        bulkPTFFileUrls
        bulkTradeFiles {
          type
          requestType
          url
        }
        allocationFiles {
          requestType
          url
          type
        }
        incompleteTradeRequestsByExchange(PTF: $PTF) {
          exchange
          total
        }
        allTradeRequestsByExchange {
          exchange
        }
      }
    }
  }
`;

const TRANSITION_BULK_TRADE_RUN = gql`
  mutation transitionBulkTradeRun($input: TransitionBulkTradeRunInput!) {
    transitionBulkTradeRun(input: $input) {
      bulkTradeRun {
        id
        state
      }
    }
  }
`;

const BulkTraderAdditionalStates = BULK_TRADER_UI_ONLY_STATES;
type BulkTraderAdditionalStates =
  (typeof BULK_TRADER_UI_ONLY_STATES)[keyof typeof BULK_TRADER_UI_ONLY_STATES];

type BulkTradeRun = {
  id: string;
  createdAt: string;
  bulkTradeFiles?: BulkTradeFileRecord[];
  allocationFiles?: AllocationFileRecord[];
  buyBulkTradeFileUrl?: string;
  sellBulkTradeFileUrl?: string;
  mutualFundFileUrl?: string;
  state: BulkTradeRunStates;
  bulkPTFFileUrls?: string[];
  incompleteTradeRequestsByExchange: {
    exchange: string;
    total: number;
  }[];
  allTradeRequestsByExchange: {
    exchange: string;
  }[];
};

const POLLING_TIME_MS = 1500;
const FIRST_STEPPER_STEP_INDEX = 0;

export type BulkTraderStep = {
  states: string[];
  label: string;
};

export const areBulkTraderStepsEqual = (
  left: BulkTraderStep[],
  right: BulkTraderStep[],
) =>
  left.length === right.length &&
  left.every(
    (step, index) =>
      step.label === right[index].label &&
      step.states.length === right[index].states.length &&
      step.states.every(
        (state, stateIndex) => state === right[index].states[stateIndex],
      ),
  );

const BulkTrader = () => {
  const { t } = useTranslation('bulkTrader');
  const params = useParams();
  const { sendAnalytic } = useContext(AnalyticsContext);
  const { enqueueSnackbar } = useSnackbar();
  const bulkTradeRunId = params.id;
  const [activeState, setActiveState] = useState<
    BulkTradeRunStates | BulkTraderAdditionalStates
  >();
  const [cancelConfirmationOpen, setCancelConfirmationOpen] = useState(false);
  const [discrepancyConfirmationOpen, setDiscrepancyConfirmationOpen] =
    useState(false);
  const [buyTradesWarnings, setBuyTradesWarnings] = useState(false);
  const [sellTradesWarnings, setSellTradesWarnings] = useState(false);
  const [tradesSaved, setTradesSaved] = useState<boolean>(false);
  const [localLoading, setLocalLoading] = useState<boolean>(false);
  const getTradeCount = (fetchedBulkTradeRun?: BulkTradeRun): number =>
    fetchedBulkTradeRun?.incompleteTradeRequestsByExchange?.reduce(
      (total, item) => total + item.total,
      0,
    ) ?? 0;
  const getAffectedAccountCount = (
    fetchedBulkTradeRun?: BulkTradeRun,
  ): number => fetchedBulkTradeRun?.allTradeRequestsByExchange?.length ?? 0;

  const getSteps = useCallback(
    ({
      hasBulkTradeOrMutualFundExchanges = false,
      hasPTFExchanges = false,
    }: {
      hasBulkTradeOrMutualFundExchanges?: boolean;
      hasPTFExchanges?: boolean;
    } = {}): BulkTraderStep[] => [
      { states: [BulkTradeRunStates.GENERATING], label: t('generating') },
      {
        states: [BulkTradeRunStates.BULK_TRADES_INITIATED],
        label: t('reviewTrades'),
      },
      ...(hasBulkTradeOrMutualFundExchanges
        ? [
            {
              states: [
                BulkTradeRunStates.BULK_TRADES_REQUESTED,
                BulkTradeRunStates.BULK_TRADES_COMPLETED,
              ],
              label: t('updateTrades'),
            },
            {
              states: [
                BulkTradeRunStates.ALLOCATIONS_REQUESTED,
                BulkTraderAdditionalStates.REVIEW_TRADE_ALLOCATIONS,
              ],
              label: t('reviewTradeAllocations'),
            },
          ]
        : []),
      ...(hasPTFExchanges
        ? [
            {
              states: [
                BulkTradeRunStates.BULK_TRADES_REQUESTED,
                BulkTradeRunStates.COMPLETED_PENDING_PTFS,
              ],
              label: t('updatePTFTrades'),
            },
            {
              states: [
                BulkTradeRunStates.ALLOCATIONS_REQUESTED,
                BulkTraderAdditionalStates.REVIEW_PTF_TRADE_ALLOCATIONS,
              ],
              label: t('reviewPTFTradeAllocations'),
            },
          ]
        : []),
      { states: [BulkTradeRunStates.COMPLETED], label: t('completed') },
    ],
    [t],
  );

  const [steps, setSteps] = useState<BulkTraderStep[]>(() => getSteps({}));
  const activeStateRef = useRef(activeState);
  activeStateRef.current = activeState;
  const terminalAnalyticsKeyRef = useRef<string | null>(null);

  const stateToStep = useCallback(
    (state: BulkTradeRunStates | BulkTraderAdditionalStates) =>
      steps.findIndex((step) => step.states.includes(state)),
    [steps],
  );
  const stateToStepRef = useRef(stateToStep);
  stateToStepRef.current = stateToStep;

  const { loading, startPolling, stopPolling, refetch, data } = useQuery<{
    fetchBulkTradeRun: { bulkTradeRun?: BulkTradeRun };
  }>(FETCH_BULK_TRADE_RUN, {
    variables: {
      bulkTradeRunId,
      PTF:
        activeState === BulkTradeRunStates.COMPLETED_PENDING_PTFS ||
        activeState === BulkTraderAdditionalStates.REVIEW_PTF_TRADE_ALLOCATIONS,
    },
    fetchPolicy: 'no-cache',
    notifyOnNetworkStatusChange: true,
  });

  const bulkTradeRun = data?.fetchBulkTradeRun?.bulkTradeRun;

  // Derive the stepper's steps and the (possibly UI-only) active state from
  // the latest polled bulk trade run.
  useEffect(() => {
    if (!bulkTradeRun) return;

    const { state } = bulkTradeRun;

    if (state !== BulkTradeRunStates.GENERATING) {
      const hasBulkTradeOrMutualFundExchanges =
        bulkTradeRun.allTradeRequestsByExchange?.some(
          (item: { exchange: string }) => item.exchange !== Exchanges.PTF,
        ) ?? false;
      const hasPTFExchanges =
        bulkTradeRun.allTradeRequestsByExchange?.some(
          (item: { exchange: string }) => item.exchange === Exchanges.PTF,
        ) ?? false;

      const nextSteps = getSteps({
        hasBulkTradeOrMutualFundExchanges,
        hasPTFExchanges,
      });
      setSteps((previousSteps) =>
        areBulkTraderStepsEqual(previousSteps, nextSteps)
          ? previousSteps
          : nextSteps,
      );
    }

    setActiveState((previousState) =>
      syncActiveStateFromBulkTradeRun(previousState, state),
    );
  }, [bulkTradeRun, getSteps]);

  // Fire terminal analytics once per bulk trade run, deduped by id + state so
  // repeated polling responses on the same terminal state don't double-fire.
  useEffect(() => {
    if (!bulkTradeRun) return;

    const { state } = bulkTradeRun;

    if (state === BulkTradeRunStates.COMPLETED) {
      const analyticsKey = `${bulkTradeRun.id}:COMPLETED`;
      if (terminalAnalyticsKeyRef.current !== analyticsKey) {
        terminalAnalyticsKeyRef.current = analyticsKey;
        sendAnalytic(analyticsEvents.bulkTraderDetailComplete, {
          bulkTradeRunId: bulkTradeRun.id,
          timestamp: new Date().toISOString(),
        });
      }
    } else if (state === BulkTradeRunStates.CANCELED) {
      const analyticsKey = `${bulkTradeRun.id}:CANCELED`;
      if (terminalAnalyticsKeyRef.current !== analyticsKey) {
        terminalAnalyticsKeyRef.current = analyticsKey;
        sendAnalytic(analyticsEvents.bulkTraderDetailCancel, {
          bulkTradeRunId: bulkTradeRun.id,
          currentState: state,
          affectedAccountCount: getAffectedAccountCount(bulkTradeRun),
          tradeCount: getTradeCount(bulkTradeRun),
          failureStep: activeStateRef.current ?? state,
          timestamp: new Date().toISOString(),
        });
      }
    }
  }, [bulkTradeRun, sendAnalytic]);

  // `stateToStepRef` (rather than `stateToStep`/`steps`) keeps this decision
  // from being recomputed merely because the steps effect above updated
  // `steps` for the same bulk trade run.
  const pollingDecision = bulkTradeRun
    ? resolveBulkTradeRunPolling(
        bulkTradeRun.state,
        stateToStepRef.current(bulkTradeRun.state),
        FIRST_STEPPER_STEP_INDEX,
      )
    : null;

  // Sole owner of the polling start/stop lifecycle, isolated from the
  // derived-state and analytics effects above so its cleanup is unconditional
  // and unambiguous. Keyed on `bulkTradeRun` (not just `pollingDecision`) so
  // it re-syncs `localLoading` on every poll response even when the decision
  // resolves to the same value on both sides of a transition — e.g. both
  // BULK_TRADES_INITIATED (awaiting the "generate files" click) and
  // BULK_TRADES_COMPLETED (awaiting review) resolve to 'stop', so a
  // manual `reloadBulkTradeWithPolling()` call in between needs this effect
  // to still run once the new state comes back, or `localLoading` would
  // stay stuck `true` from that manual call.
  useEffect(() => {
    if (pollingDecision === 'start') {
      startPolling(POLLING_TIME_MS);
      setLocalLoading(true);
    } else if (pollingDecision === 'stop') {
      stopPolling();
      setLocalLoading(false);
    }
    return () => stopPolling();
  }, [bulkTradeRun, pollingDecision, startPolling, stopPolling]);

  const [transitionBulkTradeRun, { loading: transitionLoading }] = useMutation(
    TRANSITION_BULK_TRADE_RUN,
    {
      onError: (error) => {
        sendAnalytic(analyticsEvents.bulkTraderRunError, {
          bulkTradeRunId: bulkTradeRun?.id,
          errorType: error.message,
          affectedAccountCount: getAffectedAccountCount(bulkTradeRun),
          tradeCount: getTradeCount(bulkTradeRun),
          failureStep: activeState ?? 'unknown',
          timestamp: new Date().toISOString(),
        });
      },
    },
  );

  useEffect(() => {
    if (
      bulkTradeRun &&
      activeState &&
      (
        [
          BulkTradeRunStates.BULK_TRADES_COMPLETED,
          BulkTradeRunStates.COMPLETED_PENDING_PTFS,
        ] as (BulkTradeRunStates | BulkTraderAdditionalStates)[]
      ).includes(activeState)
    ) {
      setTradesSaved(
        bulkTradeRun.incompleteTradeRequestsByExchange.length === 0,
      );
    }
  }, [activeState, bulkTradeRun]);

  const onClickNext = async () => {
    if (buyTradesWarnings || sellTradesWarnings) {
      setDiscrepancyConfirmationOpen(true);
    } else {
      await goNext();
    }
  };

  const reloadBulkTradeWithPolling = () => {
    startPolling(POLLING_TIME_MS);
    setLocalLoading(true);
  };

  const goNext = async () => {
    if (!bulkTradeRun) return;

    // Track save analytics when moving to next step after saving trades
    if (
      activeState === BulkTradeRunStates.BULK_TRADES_COMPLETED ||
      activeState === BulkTradeRunStates.COMPLETED_PENDING_PTFS
    ) {
      sendAnalytic(analyticsEvents.bulkTraderDetailSave, {
        bulkTradeRunId: bulkTradeRun.id,
        currentState: activeState,
        timestamp: new Date().toISOString(),
      });
    }

    if (activeState === BulkTradeRunStates.BULK_TRADES_COMPLETED) {
      setActiveState(BulkTraderAdditionalStates.REVIEW_TRADE_ALLOCATIONS);
      return;
    }

    if (activeState === BulkTradeRunStates.COMPLETED_PENDING_PTFS) {
      setActiveState(BulkTraderAdditionalStates.REVIEW_PTF_TRADE_ALLOCATIONS);
      return;
    }

    await transitionBulkTradeRun({
      variables: {
        input: {
          bulkTradeRunId: bulkTradeRun.id,
          transition: 'initiateAllocations',
        },
      },
    });

    reloadBulkTradeWithPolling();
  };

  const goBack = async () => {
    if (!bulkTradeRun) return;

    if (activeState === BulkTradeRunStates.BULK_TRADES_COMPLETED) {
      await transitionBulkTradeRun({
        variables: {
          input: {
            bulkTradeRunId: bulkTradeRun.id,
            transition: 'resetBulkTrades',
          },
        },
      });
      setActiveState(BulkTradeRunStates.BULK_TRADES_INITIATED);
      return;
    }

    reloadBulkTradeWithPolling();
  };

  const emitTransitionBulkTradeRun = async (
    transition: 'resetBulkTrades' | 'cancel' | 'generateBulkTradeFiles',
  ) => {
    if (!bulkTradeRun) return;

    // Track transition analytics
    sendAnalytic(analyticsEvents.bulkTraderDetailTransition, {
      bulkTradeRunId: bulkTradeRun.id,
      transition,
      currentState: activeState,
      timestamp: new Date().toISOString(),
    });

    if (transition === 'cancel') {
      sendAnalytic(analyticsEvents.bulkTraderRunAbandon, {
        bulkTradeRunId: bulkTradeRun.id,
        currentState: activeState,
        timestamp: new Date().toISOString(),
      });
    }

    await transitionBulkTradeRun({
      variables: { input: { bulkTradeRunId: bulkTradeRun.id, transition } },
    });

    reloadBulkTradeWithPolling();
  };

  return (
    <Box data-testid='bulk-trader-container'>
      {loading || !bulkTradeRun ? (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
          data-testid='bulk-trader-loading'
        >
          <CircularProgress sx={{ m: 18 }} />
        </Box>
      ) : bulkTradeRun.state === BulkTradeRunStates.CANCELED ? (
        <Typography
          variant='headingLarge'
          sx={{ textAlign: 'center', marginTop: 5, color: 'error.main' }}
          data-testid='bulk-trader-canceled'
        >
          {t('bulkTradeRunCanceled')}
        </Typography>
      ) : (
        <>
          <Box
            data-testid='bulk-trader-main-content'
            sx={{ width: '100%', mt: 5 }}
          >
            <Card variant='elevated' sx={{ width: '100%' }}>
              <Stepper
                activeStep={activeState ? stateToStep(activeState) : undefined}
                sx={{ p: 5 }}
                data-testid='bulk-trader-stepper'
              >
                {steps.map(({ label }, index) => {
                  const stepProps: { completed?: boolean } = {};
                  return (
                    <Step
                      key={label}
                      {...stepProps}
                      data-testid={`bulk-trader-step-${index}`}
                    >
                      <StepLabel
                        data-testid={`bulk-trader-step-label-${index}`}
                      >
                        {label}
                      </StepLabel>
                    </Step>
                  );
                })}
              </Stepper>

              {!activeState ||
              activeState === BulkTradeRunStates.GENERATING ||
              localLoading ? (
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    p: '350px',
                  }}
                  data-testid='bulk-trader-processing'
                >
                  <Box sx={{ textAlign: 'center' }}>
                    <CircularProgress size='xlarge' />
                  </Box>
                </Box>
              ) : (
                <>
                  {activeState === BulkTradeRunStates.BULK_TRADES_INITIATED && (
                    <Box data-testid='bulk-trader-review-trades-section'>
                      <TradeRequestsTable
                        filter={{ bulkTradeRunId: bulkTradeRun.id }}
                        showClient={true}
                        showExpected={true}
                      />
                      <Box
                        sx={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          flexWrap: 'wrap',
                        }}
                      >
                        <Button
                          variant='outlined'
                          color='error'
                          sx={{ m: 1, textAlign: 'right' }}
                          disabled={transitionLoading}
                          onClick={() => setCancelConfirmationOpen(true)}
                          data-testid='bulk-trader-cancel-button'
                        >
                          {t('cancel')}
                        </Button>
                        <Button
                          variant='contained'
                          sx={{ m: 1, textAlign: 'right' }}
                          disabled={transitionLoading}
                          onClick={() =>
                            emitTransitionBulkTradeRun('generateBulkTradeFiles')
                          }
                          data-testid='bulk-trader-next-button'
                        >
                          {t('next')}
                        </Button>
                      </Box>
                    </Box>
                  )}

                  {activeState &&
                    (
                      [
                        BulkTradeRunStates.BULK_TRADES_COMPLETED,
                        BulkTradeRunStates.COMPLETED_PENDING_PTFS,
                      ] as (BulkTradeRunStates | BulkTraderAdditionalStates)[]
                    ).includes(activeState) && (
                      <>
                        <Box data-testid='bulk-trader-update-trades-section'>
                          <Stack
                            direction='row'
                            spacing={1}
                            useFlexGap
                            sx={{ flexWrap: 'wrap', p: 1 }}
                          >
                            {bulkTradeRun.buyBulkTradeFileUrl && (
                              <ExternalLink
                                onClick={(e) => {
                                  e.stopPropagation();
                                  sendAnalytic(
                                    analyticsEvents.bulkTraderDetailDownloadBuyFile,
                                    {
                                      bulkTradeRunId: bulkTradeRun.id,
                                      fileType: 'buy',
                                      timestamp: new Date().toISOString(),
                                    },
                                  );
                                }}
                                href={bulkTradeRun.buyBulkTradeFileUrl}
                                data-testid='bulk-trader-download-buy-file'
                              >
                                {t('buyTradeFile')}
                              </ExternalLink>
                            )}
                            {bulkTradeRun.sellBulkTradeFileUrl && (
                              <ExternalLink
                                onClick={(e) => {
                                  e.stopPropagation();
                                  sendAnalytic(
                                    analyticsEvents.bulkTraderDetailDownloadSellFile,
                                    {
                                      bulkTradeRunId: bulkTradeRun.id,
                                      fileType: 'sell',
                                      timestamp: new Date().toISOString(),
                                    },
                                  );
                                }}
                                href={bulkTradeRun.sellBulkTradeFileUrl}
                                data-testid='bulk-trader-download-sell-file'
                              >
                                {t('sellTradeFile')}
                              </ExternalLink>
                            )}
                            {bulkTradeRun.mutualFundFileUrl && (
                              <ExternalLink
                                onClick={(e) => {
                                  e.stopPropagation();
                                  sendAnalytic(
                                    analyticsEvents.bulkTraderDetailDownloadMutualFundFile,
                                    {
                                      bulkTradeRunId: bulkTradeRun.id,
                                      fileType: 'mutualFund',
                                      timestamp: new Date().toISOString(),
                                    },
                                  );
                                }}
                                href={bulkTradeRun.mutualFundFileUrl}
                                data-testid='bulk-trader-download-mutual-fund-file'
                              >
                                {t('mutualFundTradeFile')}
                              </ExternalLink>
                            )}
                            {bulkTradeRun.bulkPTFFileUrls?.map(
                              (fileUrl: string, index: number) => (
                                <ExternalLink
                                  key={fileUrl}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    sendAnalytic(
                                      analyticsEvents.bulkTraderDetailDownloadPtfFile,
                                      {
                                        bulkTradeRunId: bulkTradeRun.id,
                                        fileType: 'ptf',
                                        fileIndex: index + 1,
                                        timestamp: new Date().toISOString(),
                                      },
                                    );
                                  }}
                                  href={fileUrl}
                                  data-testid={`bulk-trader-download-ptf-file-${index + 1}`}
                                >{`${t('ptfBulkTradeFile')}: ${index + 1}`}</ExternalLink>
                              ),
                            )}
                            {bulkTradeRun?.bulkTradeFiles?.map(
                              (
                                file: BulkTradeFileRecord,
                                fileIndex: number,
                              ) => {
                                const sameTypeCount =
                                  bulkTradeRun?.bulkTradeFiles
                                    ?.slice(0, fileIndex + 1)
                                    ?.filter(
                                      (f: BulkTradeFileRecord) =>
                                        f.type === file.type,
                                    ).length ?? 0;

                                const totalSameType =
                                  bulkTradeRun?.bulkTradeFiles?.filter(
                                    (f: BulkTradeFileRecord) =>
                                      f.type === file.type,
                                  ).length ?? 0;

                                const fileLabel = getBulkTradeFileDownloadLabel(
                                  file,
                                  t,
                                  sameTypeCount,
                                  totalSameType,
                                );

                                return (
                                  <ExternalLink
                                    key={file.url}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      sendAnalytic(
                                        analyticsEvents.bulkTraderDetailDownloadBulkTradeFile,
                                        {
                                          bulkTradeRunId: bulkTradeRun.id,
                                          fileType: file.type,
                                          fileIndex: fileIndex + 1,
                                          timestamp: new Date().toISOString(),
                                        },
                                      );
                                    }}
                                    href={file.url}
                                    data-testid={`bulk-trader-download-bulk-trade-file-${fileIndex}`}
                                  >
                                    {fileLabel}
                                  </ExternalLink>
                                );
                              },
                            )}
                            {bulkTradeRun?.allocationFiles?.map(
                              (
                                file: AllocationFileRecord,
                                fileIndex: number,
                              ) => {
                                const sameTypeCount =
                                  bulkTradeRun?.allocationFiles
                                    ?.slice(0, fileIndex + 1)
                                    ?.filter(
                                      (f: AllocationFileRecord) =>
                                        f.type === file.type,
                                    ).length ?? 0;

                                const totalSameType =
                                  bulkTradeRun?.allocationFiles?.filter(
                                    (f: AllocationFileRecord) =>
                                      f.type === file.type,
                                  ).length ?? 0;

                                const fileLabel =
                                  getAllocationFileDownloadLabel(
                                    file,
                                    t,
                                    sameTypeCount,
                                    totalSameType,
                                  );

                                return (
                                  <ExternalLink
                                    key={file.url}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      sendAnalytic(
                                        analyticsEvents.bulkTraderDetailDownloadAllocationFileNew,
                                        {
                                          bulkTradeRunId: bulkTradeRun.id,
                                          fileType: file.type,
                                          fileIndex: fileIndex + 1,
                                          timestamp: new Date().toISOString(),
                                        },
                                      );
                                    }}
                                    href={file.url}
                                    data-testid={`bulk-trader-download-allocation-file-${fileIndex}`}
                                  >
                                    {fileLabel}
                                  </ExternalLink>
                                );
                              },
                            )}
                          </Stack>
                          <Box
                            sx={{
                              display: 'flex',
                              flexWrap: 'wrap',
                              gap: 2,
                              p: 2,
                            }}
                          >
                            <Box sx={{ flex: '1 1 40em', minWidth: '40em' }}>
                              <Card variant='elevated' sx={{ width: '100%' }}>
                                <Box data-testid='bulk-trader-buy-trades-table'>
                                  <UpdateTradesTable
                                    bulkTradeRunId={bulkTradeRun.id}
                                    type='BUY'
                                    setDiscrepancyWarning={setBuyTradesWarnings}
                                    exchanges={
                                      activeState ===
                                      BulkTradeRunStates.COMPLETED_PENDING_PTFS
                                        ? [Exchanges.PTF]
                                        : []
                                    }
                                    onPriceUpdate={() => {
                                      sendAnalytic(
                                        analyticsEvents.bulkTraderDetailUpdate,
                                        {
                                          bulkTradeRunId: bulkTradeRun.id,
                                          updateType: 'BUY',
                                          timestamp: new Date().toISOString(),
                                        },
                                      );
                                      refetch();
                                    }}
                                  />
                                </Box>
                              </Card>
                            </Box>

                            <Box sx={{ flex: '1 1 40em', minWidth: '40em' }}>
                              <Card variant='elevated' sx={{ width: '100%' }}>
                                <Box data-testid='bulk-trader-sell-trades-table'>
                                  <UpdateTradesTable
                                    bulkTradeRunId={bulkTradeRun.id}
                                    type='SELL'
                                    setDiscrepancyWarning={
                                      setSellTradesWarnings
                                    }
                                    exchanges={
                                      activeState ===
                                      BulkTradeRunStates.COMPLETED_PENDING_PTFS
                                        ? [Exchanges.PTF]
                                        : []
                                    }
                                    onPriceUpdate={() => {
                                      sendAnalytic(
                                        analyticsEvents.bulkTraderDetailUpdate,
                                        {
                                          bulkTradeRunId: bulkTradeRun.id,
                                          updateType: 'SELL',
                                          timestamp: new Date().toISOString(),
                                        },
                                      );
                                      refetch();
                                    }}
                                  />
                                </Box>
                              </Card>
                            </Box>
                            <Box sx={{ flex: '1 1 40em', minWidth: '40em' }}>
                              <Card variant='elevated' sx={{ width: '100%' }}>
                                <Box data-testid='bulk-trader-buy-mutual-fund-trades-table'>
                                  <UpdateMutualFundTradesTable
                                    bulkTradeRunId={bulkTradeRun.id}
                                    type='BUY'
                                  />
                                </Box>
                              </Card>
                            </Box>

                            <Box sx={{ flex: '1 1 40em', minWidth: '40em' }}>
                              <Card variant='elevated' sx={{ width: '100%' }}>
                                <Box data-testid='bulk-trader-sell-mutual-fund-trades-table'>
                                  <UpdateMutualFundTradesTable
                                    bulkTradeRunId={bulkTradeRun.id}
                                    type='SELL'
                                  />
                                </Box>
                              </Card>
                            </Box>
                          </Box>
                          <Box
                            sx={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              flexWrap: 'wrap',
                              alignItems: 'flex-start',
                            }}
                          >
                            <Box>
                              {activeState ===
                                BulkTradeRunStates.BULK_TRADES_COMPLETED && (
                                <>
                                  <Button
                                    variant='outlined'
                                    color='error'
                                    sx={{ m: 1, textAlign: 'right' }}
                                    disabled={transitionLoading}
                                    onClick={() =>
                                      setCancelConfirmationOpen(true)
                                    }
                                    data-testid='bulk-trader-cancel-trades-button'
                                  >
                                    {t('cancel')}
                                  </Button>
                                  <Button
                                    variant='outlined'
                                    color='error'
                                    sx={{ m: 1, textAlign: 'right' }}
                                    disabled={transitionLoading}
                                    onClick={goBack}
                                    data-testid='bulk-trader-back-button'
                                  >
                                    {t('back')}
                                  </Button>
                                </>
                              )}
                            </Box>
                            <Box>
                              {!tradesSaved ? (
                                <Tooltip title={t('saveTradesMessage')}>
                                  <span>
                                    <Button
                                      variant='contained'
                                      sx={{ m: 1, textAlign: 'right' }}
                                      disabled={!tradesSaved}
                                      onClick={onClickNext}
                                      data-testid='bulk-trader-save-and-next-button'
                                    >
                                      {t('next')}
                                    </Button>
                                  </span>
                                </Tooltip>
                              ) : (
                                <Button
                                  variant='contained'
                                  sx={{ m: 1, textAlign: 'right' }}
                                  onClick={onClickNext}
                                  data-testid='bulk-trader-save-and-next-button'
                                >
                                  {t('next')}
                                </Button>
                              )}
                            </Box>
                          </Box>
                        </Box>
                      </>
                    )}

                  {activeState &&
                    (
                      [
                        BulkTraderAdditionalStates.REVIEW_TRADE_ALLOCATIONS,
                        BulkTraderAdditionalStates.REVIEW_PTF_TRADE_ALLOCATIONS,
                      ] as (BulkTradeRunStates | BulkTraderAdditionalStates)[]
                    ).includes(activeState) && (
                      <>
                        <Box data-testid='bulk-trader-review-allocations-section'>
                          <BulkTradeRunAllocationsTable
                            filter={{
                              bulkTradeRunId: bulkTradeRun.id,
                              isPTF:
                                activeState ===
                                BulkTraderAdditionalStates.REVIEW_PTF_TRADE_ALLOCATIONS,
                            }}
                          />
                          <Box
                            sx={{
                              display: 'flex',
                              justifyContent: 'flex-end',
                            }}
                          >
                            <Button
                              variant='contained'
                              sx={{ m: 1, textAlign: 'right' }}
                              onClick={() => goNext()}
                              data-testid='bulk-trader-complete-allocations-button'
                            >
                              {t('next')}
                            </Button>
                          </Box>
                        </Box>
                      </>
                    )}

                  {activeState === BulkTradeRunStates.COMPLETED && (
                    <>
                      <Box
                        sx={{ p: 15 }}
                        data-testid='bulk-trader-completed-section'
                      >
                        <Box
                          data-testid='bulk-trader-completed-icon'
                          sx={{
                            display: 'flex',
                            justifyContent: 'center',
                          }}
                        >
                          <CheckCircleIcon
                            color='success'
                            sx={{ fontSize: '100px' }}
                          />
                        </Box>
                        <Typography
                          variant='headingLarge'
                          sx={{
                            textAlign: 'center',
                            p: 3,
                            color: 'primary.main',
                          }}
                          data-testid='bulk-trader-completed-message'
                        >
                          {t('bulkTradeRunCompleted')}
                        </Typography>
                      </Box>
                      <Box data-testid='bulk-trader-completed-trades-table'>
                        <TradeRequestsTable
                          filter={{ bulkTradeRunId: bulkTradeRun.id }}
                          showClient={true}
                          showExpected={true}
                          bulkTradeRunState={bulkTradeRun.state}
                        />
                      </Box>
                    </>
                  )}
                </>
              )}
            </Card>
          </Box>

          <ConfirmationModal
            open={cancelConfirmationOpen}
            onCancel={() => setCancelConfirmationOpen(false)}
            onConfirm={async () => {
              await emitTransitionBulkTradeRun('cancel');
              enqueueSnackbar(t('cancelTriggeredMessage'), { variant: 'info' });
              setCancelConfirmationOpen(false);
            }}
            loading={loading}
            bodyText={t('cancelConfirmationDialog')}
            title={t('cancelBulkTradeRunTrades')}
            data-testid='bulk-trader-cancel-confirmation-modal'
          />

          <ConfirmationModal
            open={discrepancyConfirmationOpen}
            onCancel={() => setDiscrepancyConfirmationOpen(false)}
            onConfirm={() => {
              setDiscrepancyConfirmationOpen(false);
              goNext();
            }}
            title={t('discrepancyConfirmationDialog.title')}
            bodyText={t('discrepancyConfirmationDialog.text')}
            data-testid='bulk-trader-discrepancy-confirmation-modal'
          />
        </>
      )}
    </Box>
  );
};

export default BulkTrader;
